import { palette } from '@/lib/design-tokens';

import type { MapPolygonStyle } from './types';

/**
 * The **unfocused** Field Area overlay, read off Figma `787:679336` — a flattened PNG with no bound
 * variables (`get_variable_defs` returns `{}`), so its fill and dash were sampled from the 2×
 * export and matched to the nearest token. See the ticket's Design spec for every sampled number.
 *
 * Both values sit on the `accent1` ramp whose 600 step is the Field Area legend swatch
 * (`787:681186` → `Accent 1/accent-600`), which is what 113586 §1a means by the legend colour: a
 * pale tint of it, not the swatch itself, or the imagery would not read through (§1a again — the
 * overlay is translucent *so that* the satellite plate stays visible).
 *
 * The dash geometry is measured from **our** frame and differs from the admin twin's 2 px at an
 * `'8px'` pitch, which came off a different asset in a different file. Do not reconcile them.
 *
 * `fillOpacityHover` has no frame and deliberately changes **depth only**, so hovering can never be
 * mistaken for focus.
 */
export const BOUNDARY_UNFOCUSED: MapPolygonStyle = {
  fillColor: palette.accent1[200],
  fillOpacity: 0.3,
  fillOpacityHover: 0.42,
  dashColor: palette.accent1[50],
  dashOpacity: 0.72,
  dashWeight: 2,
  dashLength: 13,
  dashRepeat: '18px',
};

/**
 * The **focused** Field Area (113586 §3a). **Verified against our own frames by ticket 07** —
 * `823:701654` and `864:562852`, both flattened PNGs with no bound variables (`get_variable_defs`
 * returns `{}`), sampled from their 2× exports. The two frames agree exactly, which is what makes
 * this a verification rather than a second guess.
 *
 * | sampled | taken as |
 * | --- | --- |
 * | fill `#F0FF3E` @ 0.600 | `accent1[500]` @ `0.6` — nearest token in the palette (Δ26, all in blue) |
 * | dash `#FCFFF5` @ 1.000 | `accent2[50]` @ `1` — **exact**, no substitution |
 * | dash weight 5–6 px @2× | `3` |
 * | dash length 26.9 px @2× | `13` |
 * | dash pitch 34.6 px @2× | `'18px'` |
 *
 * The values inherited from the admin twin all held; none was changed.
 *
 * `fillOpacityHover` equals `fillOpacity` on purpose and has no frame: a focused Field is already at
 * full emphasis, and deepening it on hover would make hover and focus compete for the same signal.
 */
export const BOUNDARY_FOCUSED: MapPolygonStyle = {
  ...BOUNDARY_UNFOCUSED,
  fillColor: palette.accent1[500],
  fillOpacity: 0.6,
  fillOpacityHover: 0.6,
  dashColor: palette.accent2[50],
  dashOpacity: 1,
  dashWeight: 3,
};

/**
 * Every **other** Field Area while one is focused (113586 §3a: all other Fields are de-emphasised).
 *
 * **No Figma frame draws this state** — `823:*` and `864:*` each show one Field alone, and
 * `787:679336` is the resting state. Grilling Q11 governs where the design is silent: build from the
 * shipped tokens. So it is {@link BOUNDARY_UNFOCUSED} at **half depth** — no new colour, depth only,
 * which is the rule `fillOpacityHover` sets above: on this surface a change of treatment must never
 * read as a change of kind. FE-drafted; recorded as such in the ticket's `verification.md`.
 */
export const BOUNDARY_DEEMPHASISED: MapPolygonStyle = {
  ...BOUNDARY_UNFOCUSED,
  fillOpacity: 0.15,
  fillOpacityHover: 0.2,
  dashOpacity: 0.36,
};

/**
 * How one Field Area is painted, given which one is focused (113586 §3a).
 *
 * A rule rather than a ternary in JSX, because it is the whole of "the focused Field is highlighted
 * and **all others** are de-emphasised" — and because the two halves are easy to get subtly wrong
 * apart: with nothing focused every Field is `UNFOCUSED`, never `DEEMPHASISED`, so a map at rest and
 * a map whose focus was just cleared look identical.
 *
 * `<MapPage>` supplies the focused id from the selection reducer; `null` means nothing is focused,
 * and then every Field is `UNFOCUSED` — never `DEEMPHASISED` — so a map at rest and a map whose focus
 * was just cleared look identical (113586 §3f).
 */
export function boundaryStyleFor(fieldId: string, focusedFieldId: string | null): MapPolygonStyle {
  if (focusedFieldId === null) return BOUNDARY_UNFOCUSED;
  return fieldId === focusedFieldId ? BOUNDARY_FOCUSED : BOUNDARY_DEEMPHASISED;
}

/**
 * How one Field Area is painted on the **Remap Device page**, given which Field the Device being
 * remapped belongs to (119175 §2c, §2e).
 *
 * Deliberately **not** {@link boundaryStyleFor}, and the difference is the whole point. That rule
 * serves **Field focus** on the Map, where singling one Field out means de-emphasising every other
 * one. This screen's §2e says the opposite in as many words — all other Fields are in their
 * "non-highlighted view" — and Figma `2353:986982` agrees: the context Boundary there samples
 * `#F8FFB3` @ 0.29, which is {@link BOUNDARY_UNFOCUSED}'s own fill, not
 * {@link BOUNDARY_DEEMPHASISED}'s half-depth one.
 *
 * A named rule rather than a ternary in the page for the reason {@link boundaryStyleFor} gives
 * about its own two halves: the exclusivity half and the highlight half are easy to get subtly
 * wrong apart, and being pure and in `lib/maps/` this runs in the fast `node` test project with no
 * map to stub. `highlightedFieldId` is `null` for an **unassociated device**, and then every Field
 * is `UNFOCUSED`.
 */
export function remapBoundaryStyleFor(
  fieldId: string,
  highlightedFieldId: string | null,
): MapPolygonStyle {
  return fieldId === highlightedFieldId ? BOUNDARY_FOCUSED : BOUNDARY_UNFOCUSED;
}

/**
 * The shape actively being marked in **Field Mapping mode**, before it is saved — read off Figma
 * `594:533326` (two **Point**s, an open chain) and `594:533333` (three, closed and filled). Both are
 * flattened vectors, so the values below come from the **exported SVG bytes**, not the frame:
 * `fill="#F9FFB2" fill-opacity="0.3" stroke="#FBFCFC" stroke-width="2" stroke-dasharray="6 6"`.
 *
 * `#F9FFB2` is an **exact** match to `accent1.200` — the same tint at the same 0.3 that
 * {@link BOUNDARY_UNFOCUSED} already uses, deliberately: a Field being drawn should read as a Field.
 * What differs is the dash. A shape still being marked reads a crisp `white.100` line at full
 * opacity, where a saved Boundary settles into a softened `accent1.50`.
 *
 * `dashLength: 6` / `dashRepeat: '12px'` is the sampled 6 px-dash / 6 px-gap pair. It is a
 * **different measurement off a different asset** from {@link BOUNDARY_UNFOCUSED}'s 13 / `'18px'`
 * and {@link BOUNDARY_FOCUSED}'s — the three must never be reconciled into one number.
 *
 * `fillOpacityHover` equals `fillOpacity`: the draft outline takes no pointer events at all, so
 * there is no hover state for it to have.
 */
export const DRAFT_BOUNDARY: MapPolygonStyle = {
  fillColor: palette.accent1[200],
  fillOpacity: 0.3,
  fillOpacityHover: 0.3,
  dashColor: palette.white[100],
  dashOpacity: 1,
  dashWeight: 2,
  dashLength: 6,
  dashRepeat: '12px',
};

/**
 * The dashed **square** that bounds a circle being drawn (Figma `598:539873`) — the affordance the
 * four resize handles sit on the corners of.
 *
 * Figma draws it as a **1px solid** `rgba(255,255,255,0.5)` border with no fill, and `<MapPolygon>`
 * outlines only by repeating a one-segment dash symbol. `dashRepeat` equal to `dashLength` in px
 * makes consecutive dashes abut, which renders as a continuous hairline — so this needs no new
 * stroke mode in the shipped component. The 6 is `DRAFT_BOUNDARY`'s own measured dash length reused
 * as a segment size, not a second measurement off this asset.
 *
 * `fillOpacity: 0` because the ring inside it already carries the tint; `fillOpacityHover` matches,
 * since the box is drawn `interactive={false}` and has no hover state.
 */
export const CIRCLE_HANDLE_BOX: MapPolygonStyle = {
  fillColor: palette.white[100],
  fillOpacity: 0,
  fillOpacityHover: 0,
  dashColor: palette.white[100],
  dashOpacity: 0.5,
  dashWeight: 1,
  dashLength: 6,
  dashRepeat: '6px',
};

/**
 * The **Imported shape** a Grower has selected on the **Import preview** (113609) — read off the
 * **exported SVG bytes** of Figma `1657:791485`, not a screenshot:
 * `fill="#00A2FF" fill-opacity="0.6" stroke="#FCFFF5" stroke-opacity="0.7" stroke-width="2"
 * stroke-dasharray="17 17"`. Sampling the flattened frame gives a composite over satellite imagery
 * (the grilling's `≈#42A0B7`); the bytes give the fill itself.
 *
 * | sampled | taken as |
 * | --- | --- |
 * | fill `#00A2FF` @ 0.600 | `info[700]` @ `0.6` — nearest token in the whole palette (Δ72.5; runner-up `info[500]` at Δ109.7) |
 * | dash `#FCFFF5` @ 0.700 | `white[100]` @ `0.7` — Δ7.6. The bytes are exactly `accent2[50]`, but the AC and grilling decision 6 both pin a `white[100]` dash matching {@link DRAFT_BOUNDARY}, and the two are indistinguishable at 0.7 over imagery |
 * | stroke weight 2 | `2` |
 * | dash 17 / gap 17 | `17` / `'34px'` |
 *
 * **This is the one place in this app where a Field-shaped overlay changes _kind_ rather than
 * depth**, and it is the one place that is correct: on the preview surface these are not Fields yet
 * (grilling Q6). Every other treatment here sits on `accent1`, whose governing rule
 * {@link BOUNDARY_UNFOCUSED} states — an **Imported shape** is not a Field Area, so the rule does
 * not reach it. **No palette entry was added.**
 *
 * `dashLength: 17` / `dashRepeat: '34px'` is a **fourth independent measurement** off a fourth
 * asset. It must never be reconciled with {@link BOUNDARY_UNFOCUSED}'s 13 / `'18px'` or
 * {@link DRAFT_BOUNDARY}'s 6 / `'12px'`.
 *
 * `fillOpacityHover` equals `fillOpacity`: a selected shape is already at full emphasis, and on the
 * single-shape preview it is the only shape there is.
 */
export const IMPORT_SELECTED: MapPolygonStyle = {
  fillColor: palette.info[700],
  fillOpacity: 0.6,
  fillOpacityHover: 0.6,
  dashColor: palette.white[100],
  dashOpacity: 0.7,
  dashWeight: 2,
  dashLength: 17,
  dashRepeat: '34px',
};

/**
 * How one **Imported shape** is painted on the **Import preview**, given whether the file put it
 * within reach and whether it is the Grower's choice (113609).
 *
 * A rule rather than a ternary in JSX, for the reason {@link boundaryStyleFor} gives about its own
 * two halves: "highlight **only** the selected shape" is a statement about exclusivity, and the
 * exclusive half and the highlight half are easy to get subtly wrong apart. Being pure and in
 * `lib/maps/` it also runs in the fast `node` test project, with no map to stub.
 *
 * `selectable` is tested **first**. An unselectable shape can never be the selection — the page
 * never offers it one — but if a caller ever says otherwise, de-emphasis must win rather than paint
 * an unpickable shape as the Grower's choice.
 *
 * A single **named** object rather than two positional booleans, and structural rather than an
 * `ImportedShape` — the same reason {@link BoundaryRings} is declared structurally in
 * `boundaries.ts`: `eslint-plugin-boundaries` lets `lib` import only `lib` and `types`, and
 * `ImportedShape` lives in `features/fields/schemas.ts`. Named because two adjacent booleans of the
 * same type are a permanent swap hazard that neither the compiler nor a reader of
 * `importCandidateStyle(false, true)` can catch.
 *
 * There is no fourth branch and no `kind` parameter. A shapefile hole is this glossary's **Interior
 * shape** and is offered as an ordinary candidate with no badge, label or nesting affordance
 * (grilling L12) — `importedShapeSchema` does not even model `kind`.
 */
export function importCandidateStyle({
  selectable,
  selected,
}: {
  selectable: boolean;
  selected: boolean;
}): MapPolygonStyle {
  if (!selectable) return BOUNDARY_DEEMPHASISED;
  return selected ? IMPORT_SELECTED : DRAFT_BOUNDARY;
}
